import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react'
import { CheckCircle, Info, WarningCircle, X, type IconProps } from './icons'
import { addToast, autoDismissDelay, removeToast, type ToastItem, type ToastTone } from './toast-queue'

export type ToastOptions = { description?: string }

export type ToastApi = {
  success: (title: string, options?: ToastOptions) => string
  error: (title: string, options?: ToastOptions) => string
  info: (title: string, options?: ToastOptions) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const TONE_ICON: Record<ToastTone, ComponentType<IconProps>> = {
  success: CheckCircle,
  error: WarningCircle,
  info: Info,
}

let toastCounter = 0

/**
 * Shows action feedback (saved, sent, failed) as toasts in the top-right corner.
 * Errors stack above other toasts and stay until dismissed. Mount it once near the app root.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: string) => setToasts((queue) => removeToast(queue, id)), [])

  const api = useMemo<ToastApi>(() => {
    const show = (tone: ToastTone) => (title: string, options?: ToastOptions) => {
      toastCounter += 1
      const id = `toast-${toastCounter}`
      setToasts((queue) => addToast(queue, { id, tone, title, description: options?.description }))
      return id
    }
    return { success: show('success'), error: show('error'), info: show('info'), dismiss }
  }, [dismiss])

  // Newest first inside each list. The two lists are live regions that stay
  // mounted, so screen readers announce each toast when it is added.
  const errors = toasts.filter((toast) => toast.tone === 'error').reverse()
  const others = toasts.filter((toast) => toast.tone !== 'error').reverse()

  return (
    <ToastContext.Provider value={api}>
      {children}
      <section className="ui-toast-viewport" aria-label="Notifications">
        <ol className="ui-toast-list" aria-live="assertive" aria-relevant="additions" aria-atomic="false">
          {errors.map((toast) => <ToastView key={toast.id} toast={toast} onDismiss={dismiss} />)}
        </ol>
        <ol className="ui-toast-list" aria-live="polite" aria-relevant="additions" aria-atomic="false">
          {others.map((toast) => <ToastView key={toast.id} toast={toast} onDismiss={dismiss} />)}
        </ol>
      </section>
    </ToastContext.Provider>
  )
}

function ToastView({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: string) => void }) {
  const [paused, setPaused] = useState(false)
  const delay = autoDismissDelay(toast.tone)

  // The timer pauses while the pointer or keyboard focus is on the toast, and
  // restarts in full when it leaves, so people have time to read it.
  useEffect(() => {
    if (delay === null || paused) return
    const timer = window.setTimeout(() => onDismiss(toast.id), delay)
    return () => window.clearTimeout(timer)
  }, [delay, paused, toast.id, onDismiss])

  const Icon = TONE_ICON[toast.tone]
  return (
    <li
      className={`ui-toast ui-toast--${toast.tone}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPaused(false) }}
    >
      <span className="ui-toast__tile" aria-hidden="true"><Icon size={18} /></span>
      <div className="ui-toast__text">
        <p className="ui-toast__title">{toast.title}</p>
        {toast.description && <p className="ui-toast__description">{toast.description}</p>}
      </div>
      <button type="button" className="ui-icon-button ui-toast__close" aria-label="Dismiss Notification" onClick={() => onDismiss(toast.id)}>
        <X size={16} aria-hidden="true" />
      </button>
    </li>
  )
}

/** Returns toast.success, toast.error, toast.info and toast.dismiss. Needs a ToastProvider above it. */
export function useToast(): ToastApi {
  const api = useContext(ToastContext)
  if (!api) throw new Error('useToast needs a ToastProvider above it in the tree.')
  return api
}
